import { GlobalStyles, Stack, useMediaQuery } from "@mui/material";
import { PendingLeaderboardTable } from "components/events/judging/pending/PendingLeaderboard";
import { PendingLeaderboardDrawerHeader } from "components/events/judging/pending/PendingLeaderboardHeader";
import { usePendingLeaderboard } from "components/events/judging/pending/usePendingLeaderboard";
import { GetPendingLeaderboardsQueryVariables } from "components/events/judging/pending/GetPendingLeaderboards.gql.types";
import { EmptyState } from "components/layout/EmptyState";
import { REMOTE_CONTROL_SPACER_HEIGHT } from "components/layout/event/RemoteControlSpacer";
import { HeatBreakdownSkeleton } from "components/layout/heat/HeatBreakdownSkeleton";
import { useFeatureFlags } from "components/providers/featureFlags/useFeatureFlags";
import { IconEmptyLeaderboard } from "styleguide/icons/icons";
import { Sx } from "utils/sx";
import { mergeSx } from "utils/sx";

const drawerStyles: Sx = (theme) => ({
    width: theme.spacing(40),
    borderLeft: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: "60px",
    right: 0,
    bottom: 0,
    zIndex: 3,
    backgroundColor: theme.palette.background.paper,
    marginBottom: `-${REMOTE_CONTROL_SPACER_HEIGHT}`,

    [theme.breakpoints.down("sm")]: {
        width: "100%",
        position: "fixed",
        zIndex: theme.zIndex.drawer,
        overflow: "auto",
    },
});

const mobileGlobalStyles = <GlobalStyles styles={{ body: { overflow: "hidden" } }} />; // MUI recommends setting global styles in a constant

export const PendingLeaderboardDrawer: React.FC<
    {
        isOpen: boolean;
        close: () => void;
    } & GetPendingLeaderboardsQueryVariables
> = ({ isOpen, close, round, eventDivisionId }) => {
    const {
        query: { loading },
        leaderboard,
        leaderboardWithPending,
        progression,
    } = usePendingLeaderboard({ eventDivisionId, round });
    
    const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
    const isEnabled = useFeatureFlags("Skate judge");

    if (!isEnabled) return null;

    if (!leaderboard?.result.length || !leaderboardWithPending?.result.length) {
        return <Stack sx={mergeSx(drawerStyles, { display: isOpen ? "block" : "none" })} aria-hidden={!isOpen} data-testid="pending-leaderboard-drawer">
            {isMobile && isOpen && mobileGlobalStyles}
            <PendingLeaderboardDrawerHeader closeDrawer={close} />
            {loading ? <HeatBreakdownSkeleton /> : <EmptyState
                icon={<IconEmptyLeaderboard />}
                text="The round leaderboard will appear here once there's some scores"
            />}
        </Stack>;
    }
    
    return (
        <Stack sx={mergeSx(drawerStyles, { display: isOpen ? "block" : "none" })} aria-hidden={!isOpen} data-testid="pending-leaderboard-drawer">
            {isMobile && isOpen && mobileGlobalStyles}
            <PendingLeaderboardTable
                Header={<PendingLeaderboardDrawerHeader closeDrawer={close} />}
                leaderboard={leaderboard}
                leaderboardWithPending={leaderboardWithPending}
                progression={progression}
            />
        </Stack>
    );
};
